<template>
  <div class="z-100 sticky top-0 group/nav self-start w-full md:w-16 md:shrink-0 md:min-h-screen">
    <!-- Mobile Top Bar (visible only on mobile) -->
    <div class="mobile-topbar flex w-full items-center justify-between p-4 md:hidden bg-white z-50">
      <img src="/img/logotype.svg" alt="logo" class="h-8" />
      <Button icon="pi pi-bars" @click="mobileMenuVisible = true" text rounded />
    </div>

    <!-- Desktop Sidebar (visible only on desktop) - overlays content on expand -->
    <div
      class="sidebar absolute top-0 left-0 z-50 h-full min-h-screen hidden md:block w-16 group-hover/nav:w-60 transition-[width] duration-200 ease-out overflow-hidden"
    >
      <Menu
        :model="items"
        class="w-full sticky top-0 left-0 h-screen rounded-none border-r"
      >
        <template #start>
          <div class="relative flex items-center px-3.5 group-hover/nav:px-8 py-6 h-20 transition-[padding] duration-200">
            <img
              src="/img/logo.svg"
              alt="logo"
              class="h-8 w-8 object-contain opacity-100 group-hover/nav:opacity-0 transition-opacity duration-200"
            />
            <img
              src="/img/logotype.svg"
              alt="logo"
              class="absolute left-0 group-hover/nav:left-4 top-1/2 -translate-y-1/2 h-10 opacity-0 group-hover/nav:opacity-100 transition-[opacity,left] duration-200"
            />
          </div>
        </template>
        <template #item="{ item, props }">
          <RouterLink
            v-if="item.to"
            :to="item.to"
            class="flex items-center rounded-md whitespace-nowrap"
            v-bind="props.action"
          >
            <span :class="item.icon" />
            <span
              class="ml-2 opacity-0 group-hover/nav:opacity-100 transition-opacity duration-200"
            >
              {{ item.label }}
            </span>
          </RouterLink>
        </template>
      </Menu>

      <!-- Secondary navigation pinned to the bottom, set apart from the main menu -->
      <div class="absolute bottom-0 left-0 z-10 w-full pb-6">
        <Menu :model="bottomItems" class="w-full rounded-none border-x-0 border-b-0 border-t">
          <template #item="{ item, props }">
            <RouterLink
              v-if="item.to"
              :to="item.to"
              class="flex items-center rounded-md whitespace-nowrap"
              v-bind="props.action"
              :data-cy="item.dataCy"
            >
              <span :class="item.icon" />
              <span
                class="ml-2 opacity-0 group-hover/nav:opacity-100 transition-opacity duration-200"
              >
                {{ item.label }}
              </span>
            </RouterLink>
          </template>
        </Menu>
      </div>

      <div
        class="left-1/2 -translate-x-1/2 bottom-0 absolute text-grey-300 opacity-0 group-hover/nav:opacity-100 transition-opacity duration-200"
      >
        {{ version }}
      </div>
    </div>

    <!-- Mobile Drawer Menu -->
    <Drawer v-model:visible="mobileMenuVisible" position="right" class="md:hidden w-full">
      <template #header>
        <div class="flex items-center">
          <img
            src="/img/logotype.svg"
            alt="logo"
            class="h-8 transition-opacity duration-500 ease-in-out"
            :class="logoVisible ? 'opacity-100' : 'opacity-0'"
          />
        </div>
      </template>
      <div class="flex h-full flex-col">
        <Menu :model="items" class="w-full border-none">
          <template #item="{ item, props }">
            <RouterLink
              v-if="item.to"
              :to="item.to"
              class="flex items-center rounded-md"
              v-bind="props.action"
              @click="mobileMenuVisible = false"
            >
              <span :class="item.icon" />
              <span>{{ item.label }}</span>
            </RouterLink>
          </template>
        </Menu>

        <!-- Secondary navigation, set apart at the bottom of the drawer -->
        <Menu
          :model="bottomItems"
          class="mt-auto w-full rounded-none border-x-0 border-b-0 border-t"
        >
          <template #item="{ item, props }">
            <RouterLink
              v-if="item.to"
              :to="item.to"
              class="flex items-center rounded-md"
              v-bind="props.action"
              :data-cy="item.dataCy"
              @click="mobileMenuVisible = false"
            >
              <span :class="item.icon" />
              <span>{{ item.label }}</span>
            </RouterLink>
          </template>
        </Menu>
      </div>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Menu from 'primevue/menu'
import Button from 'primevue/button'
import Drawer from 'primevue/drawer'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { hasPermission } from '@/utils/utils'
import { Permission } from '@/constant'
import type { AuthUser } from '@/services/authServices'

const authStore = useAuthStore()
const { t } = useI18n()
const router = useRouter()
const mobileMenuVisible = ref(false)
const logoVisible = ref(false)
const version: string = __APP_VERSION__

// Watch for drawer visibility changes
watch(mobileMenuVisible, (newVal) => {
  if (newVal) {
    // Drawer is opening - reset logo to invisible, then fade in after 250ms
    logoVisible.value = false
    setTimeout(() => {
      logoVisible.value = true
    }, 50)
  } else {
    // Drawer is closing - reset logo state
    logoVisible.value = false
  }
})

// Use computed to make items reactive to language changes
const items = computed(() => {
  const user = authStore.user as AuthUser | null

  const rawItems = [
    {
      separator: true,
    },
    {
      label: t('nav.dashboard'),
      icon: 'pi pi-home',
      to: '/',
    },
    {
      label: t('nav.users'),
      icon: 'pi pi-users',
      to: '/users',
      // Same permissions as route meta in router/index.ts
      requiredPermission: [
        Permission.CREATE_OWN_CUSTOMER_USER,
        Permission.READ_OWN_CUSTOMER_USER,
        Permission.UPDATE_OWN_CUSTOMER_USER,
        Permission.DELETE_OWN_CUSTOMER_USER,
      ],
    },
    {
      label: t('nav.tags'),
      icon: 'pi pi-tags',
      to: '/tags',
      requiredPermission: [
        Permission.READ_TAG,
        Permission.CREATE_TAG,
        Permission.UPDATE_TAG,
        Permission.DELETE_TAG,
      ],
    },
    {
      label: t('nav.items'),
      icon: 'pi pi-list',
      to: '/items',
      requiredPermission: [
        Permission.CREATE_SET,
        Permission.READ_SET,
        Permission.UPDATE_SET,
        Permission.DELETE_SET,
        Permission.READ_ITEM,
      ],
    },
    {
      label: t('nav.myPlans'),
      icon: 'pi pi-clipboard',
      to: '/my-plans',
      requiredPermission: [Permission.READ_PLANNING],
    },
    {
      separator: false,
    },
  ]

  // Filter out items the user is not allowed to see (separators are kept).
  // An admin-hub impersonation session sees every section.
  return rawItems.filter(
    (item: any) =>
      item.separator ||
      authStore.isAdminHubSession ||
      !item.requiredPermission ||
      hasPermission(user, item.requiredPermission),
  )
})

// Bottom-of-sidebar entries, separated from the primary navigation. The manual
// is available to every ExhibitorHub user, so no permission filtering here.
const bottomItems = computed(() => [
  {
    label: t('nav.manual'),
    icon: 'pi pi-question-circle',
    to: '/manual',
    dataCy: 'nav-manual',
  },
])
</script>

<style scoped>
.sidebar :deep(.p-menu-item-link) {
  padding-left: 16px;
}
</style>
